export interface ReviewedVariance {
  variance_amount: number;
  review_reason: string;
}

function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function errorMessage(error: any): string {
  return String(error?.body?.error || error?.error || error?.message || '');
}

/**
 * Parses a confirm mismatch message and derives its signed variance. Handles
 * BOTH shapes — combined payments ("…vs invoices total X") and single
 * invoices ("…vs invoice X") — so fee-adjusted single payments stay
 * confirmable from the UI, not just combined ones.
 */
export function parseGroupVariance(error: any): ReviewedVariance | null {
  const match = errorMessage(error).match(
    /Amount mismatch: transaction (-?[\d,.]+) vs (?:invoices total|invoice) (-?[\d,.]+)/,
  );
  if (!match) return null;
  const transaction = Number(match[1].replace(/,/g, ''));
  const subtotal = Number(match[2].replace(/,/g, ''));
  if (!Number.isFinite(transaction) || !Number.isFinite(subtotal)) return null;
  return { variance_amount: round2(transaction - subtotal), review_reason: '' };
}

/** Asks for a reason and returns null when the reviewer cancels or leaves it blank. */
export function promptReviewedVariance(error: any): ReviewedVariance | null {
  const parsed = parseGroupVariance(error);
  if (!parsed) return null;
  const label = `This payment differs by ${parsed.variance_amount.toFixed(2)}. Enter the approved variance reason:`;
  const reason = window.prompt(label, '')?.trim() || '';
  if (!reason) return null;
  return { ...parsed, review_reason: reason };
}

/** Asks for the audit reason required before a reversible match undo. */
export function promptUndoReason(): string | null {
  const reason = window.prompt('Enter the reason for unlinking this payment:', '')?.trim() || '';
  return reason || null;
}
